import React, { useState, useEffect } from "react";
import { Modal, Form, Row, Col, Table, Alert, Badge, Card } from "react-bootstrap";
import Select from "react-select";
import { ButtonComponent } from "../../../../components/elements";
import axios from "axios";
import Swal from "sweetalert2";

const ExchangeModal = ({
    show,
    onHide,
    saleId,
    saleProducts,
    saleReturns,
    availableProducts,
    paymentMethods,
    authToken,
    onSuccess,
    t
}) => {
    const getTodayDate = () => {
        const today = new Date();
        return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    };

    const formatAmount = (amount) => {
        const num = Number(amount);
        return Number.isInteger(num) ? num.toString() : num.toFixed(2);
    };

    const [exchangeReturnProducts, setExchangeReturnProducts] = useState([]);
    const [exchangeNewProducts, setExchangeNewProducts] = useState([]);
    const [exchangeSubmitting, setExchangeSubmitting] = useState(false);
    const [exchangeReturnReason, setExchangeReturnReason] = useState('');
    const [exchangeReturnDate, setExchangeReturnDate] = useState(getTodayDate());
    const [exchangePaymentMethodId, setExchangePaymentMethodId] = useState('');
    const [exchangeErrors, setExchangeErrors] = useState({});

    useEffect(() => {
        if (show && saleProducts.length > 0) {
            const remainingProducts = saleProducts.map(product => {
                const returnedQty = saleReturns
                    .filter(ret => ret.product_id === product.product_id)
                    .reduce((sum, ret) => sum + ret.quantity, 0);

                return {
                    product_id: product.product_id,
                    product_name: product.product_name,
                    max_quantity: product.quantity,
                    quantity: 0,
                    sale_price: product.sale_price,
                    discount_price: product.discount_price,
                    extra_price: product.extra_price,
                };
            }).filter(p => p.max_quantity > 0);

            setExchangeReturnProducts(remainingProducts);
            setExchangeNewProducts([]);
            setExchangeReturnReason('');
            setExchangeReturnDate(getTodayDate());
            setExchangePaymentMethodId('');
            setExchangeErrors({});
        }
    }, [show, saleProducts, saleReturns]);

    const updateExchangeReturnQuantity = (index, quantity) => {
        const updated = [...exchangeReturnProducts];
        const qty = Math.min(Math.max(0, parseInt(quantity) || 0), updated[index].max_quantity);
        updated[index].quantity = qty;
        setExchangeReturnProducts(updated);
    };

    const getAvailableProductsForExchange = (currentIndex) => {
        const selectedProductIds = exchangeNewProducts
            .filter((_, idx) => idx !== currentIndex)
            .map(p => p.product_id)
            .filter(id => id);
        return availableProducts.filter(product => !selectedProductIds.includes(product.product_id));
    };

    const addExchangeNewProduct = () => {
        setExchangeNewProducts([
            ...exchangeNewProducts,
            {
                id: Date.now(),
                product_id: '',
                product_name: '',
                quantity: 1,
                sale_price: 0,
                extra_price: 0,
                discount_price: 0,
                available_stock: 0
            }
        ]);
    };

    const updateExchangeNewProduct = (index, field, value) => {
        const updated = [...exchangeNewProducts];
        if (field === 'product_id') {
            const selectedProduct = availableProducts.find(p => p.product_id === parseInt(value));
            if (selectedProduct) {
                updated[index].product_id = selectedProduct.product_id;
                updated[index].product_name = selectedProduct.product_name;
                updated[index].sale_price = selectedProduct.sale_price || 0;
                updated[index].available_stock = selectedProduct.stock_qty || 0;
            }
        } else if (field === 'quantity') {
            let qty = Math.max(1, parseInt(value) || 1);
            const maxStock = updated[index].available_stock || 0;
            if (qty > maxStock && maxStock > 0) qty = maxStock;
            updated[index].quantity = qty;
        } else if (field === 'sale_price' || field === 'extra_price' || field === 'discount_price') {
            updated[index][field] = parseFloat(value) || 0;
        }
        setExchangeNewProducts(updated);
    };

    const removeExchangeNewProduct = (index) => {
        setExchangeNewProducts(exchangeNewProducts.filter((_, i) => i !== index));
    };

    const calculateExchangeReturnTotal = () => {
        return exchangeReturnProducts.reduce((sum, item) => sum + (item.quantity * (item.sale_price - item.discount_price + item.extra_price)), 0);
    };

    const calculateExchangeNewTotal = () => {
        return exchangeNewProducts.reduce((sum, item) => {
            const lineTotal = (item.sale_price * item.quantity) - item.discount_price + item.extra_price;
            return sum + lineTotal;
        }, 0);
    };

    const handleExchangeSubmit = async () => {
        const selectedReturnProducts = exchangeReturnProducts.filter(p => p.quantity > 0);
        const validNewProducts = exchangeNewProducts.filter(p => p.product_id && p.quantity > 0);



        const returnTotal = calculateExchangeReturnTotal();
        const newTotal = calculateExchangeNewTotal();

        if (newTotal > returnTotal && !exchangePaymentMethodId) {
            Swal.fire({ icon: 'warning', title: t("Warning"), text: t("Additional payment required. Please select a payment method.") });
            return;
        }

        setExchangeSubmitting(true);
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/sales/exchange`,
                {
                    old_sale_id: parseInt(saleId),
                    new_sale_products: validNewProducts.map(p => ({
                        product_id: p.product_id,
                        quantity: p.quantity,
                        sale_price: p.sale_price,
                        extra_price: p.extra_price,
                        discount_price: p.discount_price
                    })),
                    return_products: selectedReturnProducts.map(p => ({ product_id: p.product_id, quantity: p.quantity })),
                    return_reason: exchangeReturnReason,
                    return_date: exchangeReturnDate,
                    payment_method_id: exchangePaymentMethodId || null,
                    payment_date: exchangeReturnDate,
                },
                { headers: { Authorization: `Bearer ${authToken}` } }
            );

            if (response.data.status) {
                const { old_total, new_total, adjusted, remaining, refund } = response.data;
                Swal.fire({
                    icon: 'success',
                    title: t("Exchange Successful"),
                    html: `
                        <div style="text-align: left; background: #f8f9fa; padding: 12px; border-radius: 6px; font-size: 14px; color:#333;">
                            <div style="display:flex; justify-content:space-between; margin-bottom:4px;"><span>${t("Old Invoice Total")}:</span> <b>${formatAmount(old_total)}</b></div>
                            <div style="display:flex; justify-content:space-between; margin-bottom:4px;"><span>${t("New Order Total")}:</span> <b>${formatAmount(new_total)}</b></div>
                            <div style="display:flex; justify-content:space-between; margin-bottom:4px; border-top:1px dashed #ccc; padding-top:4px;"><span>${t("Adjusted Value")}:</span> <b style="color: #0dcaf0;">${formatAmount(adjusted)}</b></div>
                            <div style="display:flex; justify-content:space-between; margin-bottom:4px;"><span>${t("Remaining Balance")}:</span> <b style="color: #dc3545;">${formatAmount(remaining)}</b></div>
                            <div style="display:flex; justify-content:space-between;"><span>${t("Refund")}:</span> <b style="color: #198754;">${formatAmount(refund)}</b></div>
                        </div>
                    `,
                });
                onHide();
                if (onSuccess) onSuccess();
            }
        } catch (err) {
            if (err.response?.status === 422) setExchangeErrors(err.response.data.errors || {});
            Swal.fire({ icon: 'error', title: t("Error"), text: err.response?.data?.message || t("Failed to complete exchange") });
        } finally {
            setExchangeSubmitting(false);
        }
    };

    const exchangeReturnTotal = calculateExchangeReturnTotal();
    const exchangeNewTotal = calculateExchangeNewTotal();
    const exchangeBalance = exchangeNewTotal - exchangeReturnTotal;

    return (
        <Modal show={show} onHide={onHide} size="xl" centered contentClassName="border-0 shadow-lg" style={{ borderRadius: "12px" }}>
            <Modal.Header closeButton className="bg-dark text-white p-3">
                <Modal.Title className="fw-bold fs-5 text-white">🔄 {t("Product Exchange Desk")}</Modal.Title>
            </Modal.Header>

            <Modal.Body className="bg-light p-4">
                <Form>
                    {/* SECTION 1: Return Items */}
                    <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: "8px" }}>
                        <Card.Body className="p-3">
                            <h6 className="text-warning fw-bold mb-3">{t("1. Products to Return")}</h6>
                            {exchangeReturnProducts.length === 0 ? (
                                <Alert variant="warning" className="mb-0 py-2">{t("No products available for return.")}</Alert>
                            ) : (
                                <div className="table-responsive">
                                    <Table hover size="sm" className="align-middle mb-0">
                                        <thead className="table-light text-secondary">
                                            <tr>
                                                <th className="py-2 border-0">{t("Product Name")}</th>
                                                <th className="text-center border-0" style={{ width: "120px" }}>{t("Max Returnable")}</th>
                                                <th className="text-center border-0" style={{ width: "120px" }}>{t("Qty to Return")}</th>
                                                <th className="text-end border-0 pe-3" style={{ width: "140px" }}>{t("Refund Subtotal")}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {exchangeReturnProducts.map((product, index) => (
                                                <tr key={index}>
                                                    <td className="fw-semibold py-2">{product.product_name}</td>
                                                    <td className="text-center">
                                                        <Badge bg="secondary" className="px-2 py-1">{product.max_quantity} pcs</Badge>
                                                    </td>
                                                    <td className="text-center">
                                                        <input
                                                            type="number"
                                                            className="form-control form-control-sm text-center mx-auto border-secondary-subtle"
                                                            min="0"
                                                            max={product.max_quantity}
                                                            placeholder="0"
                                                            value={product.quantity === 0 ? "" : product.quantity}
                                                            onChange={(e) => updateExchangeReturnQuantity(index, e.target.value)}
                                                            style={{ width: "80px" }}
                                                        />
                                                    </td>
                                                    <td className="text-end text-danger fw-bold pe-3">
                                                        {formatAmount(product.quantity * (product.sale_price - product.discount_price + product.extra_price))}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                        <tfoot className="table-light fw-bold">
                                            <tr className="border-top">
                                                <td colSpan="3" className="text-end py-2 text-secondary">{t("Total Credit Generated")}:</td>
                                                <td className="text-end text-danger pe-3 py-2">{formatAmount(exchangeReturnTotal)}</td>
                                            </tr>
                                        </tfoot>
                                    </Table>
                                </div>
                            )}
                        </Card.Body>
                    </Card>

                    {/* SECTION 2: New Items */}
                    <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: "8px" }}>
                        <Card.Body className="p-3">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h6 className="text-success fw-bold mb-0">{t("2. New Products to Issue")}</h6>
                                <ButtonComponent
                                    type="button"
                                    className="btn btn-success btn-sm px-3"
                                    onClick={addExchangeNewProduct}
                                    text={t("+ Add New Item")}
                                />
                            </div>
                            <div className="table-responsive">
                                <Table hover size="sm" className="align-middle mb-0">
                                    <thead className="table-light text-secondary">
                                        <tr>
                                            <th className="py-2 border-0">{t("Select Product")}</th>
                                            <th className="text-center border-0" style={{ width: "90px" }}>{t("Quantity")}</th>
                                            <th className="text-end border-0" style={{ width: "110px" }}>{t("Unit Price")}</th>
                                            <th className="text-end border-0" style={{ width: "110px" }}>{t("Extra")}</th>
                                            <th className="text-end border-0" style={{ width: "110px" }}>{t("Disc.")}</th>
                                            <th className="text-end border-0" style={{ width: "130px" }}>{t("Total Cost")}</th>
                                            <th className="text-center border-0" style={{ width: "40px" }}></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {exchangeNewProducts.length === 0 ? (
                                            <tr>
                                                <td colSpan="7" className="text-center text-muted py-3 small">
                                                    {t("No replacement items selected yet.")}
                                                </td>
                                            </tr>
                                        ) : (
                                            exchangeNewProducts.map((product, index) => (
                                                <tr key={product.id}>
                                                    <td className="py-1">
                                                        <Select
                                                            classNamePrefix="react-select"
                                                            options={getAvailableProductsForExchange(index).map(p => ({
                                                                value: p.product_id,
                                                                label: `${p.product_name} (Stock: ${p.stock_qty || 0})`
                                                            }))}
                                                            value={product.product_id ? { value: product.product_id, label: product.product_name } : null}
                                                            onChange={(selected) => updateExchangeNewProduct(index, 'product_id', selected ? selected.value : '')}
                                                            placeholder={t("Search and pick items...")}
                                                            isSearchable
                                                            styles={{ control: (base) => ({ ...base, minHeight: '31px', borderRadius: '4px' }) }}
                                                        />
                                                    </td>
                                                    <td className="text-center">
                                                        <input
                                                            type="number"
                                                            className="form-control form-control-sm text-center mx-auto"
                                                            min="1"
                                                            value={product.quantity}
                                                            onChange={(e) => updateExchangeNewProduct(index, 'quantity', e.target.value)}
                                                            style={{ width: "70px" }}
                                                        />
                                                    </td>
                                                    <td className="text-end">
                                                        <input
                                                            type="number"
                                                            className="form-control form-control-sm text-end ms-auto"
                                                            step="0.01"
                                                            value={product.sale_price}
                                                            onChange={(e) => updateExchangeNewProduct(index, 'sale_price', e.target.value)}
                                                            style={{ width: "95px" }}
                                                        />
                                                    </td>
                                                    <td className="text-end">
                                                        <input
                                                            type="number"
                                                            className="form-control form-control-sm text-end ms-auto text-info fw-semibold"
                                                            step="0.01"
                                                            min="0"
                                                            value={product.extra_price}
                                                            onChange={(e) => updateExchangeNewProduct(index, 'extra_price', e.target.value)}
                                                            style={{ width: "95px" }}
                                                        />
                                                    </td>
                                                    <td className="text-end">
                                                        <input
                                                            type="number"
                                                            className="form-control form-control-sm text-end ms-auto text-danger fw-semibold"
                                                            step="0.01"
                                                            min="0"
                                                            value={product.discount_price}
                                                            onChange={(e) => updateExchangeNewProduct(index, 'discount_price', e.target.value)}
                                                            style={{ width: "95px" }}
                                                        />
                                                    </td>
                                                    <td className="text-end fw-bold text-success pe-2">
                                                        {formatAmount((product.sale_price * product.quantity) - product.discount_price + product.extra_price)}
                                                    </td>
                                                    <td className="text-center">
                                                        <button type="button" className="btn btn-link link-danger p-0 border-0" onClick={() => removeExchangeNewProduct(index)}>✕</button>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                    <tfoot className="table-light fw-bold">
                                        <tr className="border-top">
                                            <td colSpan="5" className="text-end py-2 text-secondary">{t("Total New Costs")}:</td>
                                            <td className="text-end text-success py-2 pe-2">{formatAmount(exchangeNewTotal)}</td>
                                            <td></td>
                                        </tr>
                                    </tfoot>
                                </Table>
                            </div>
                        </Card.Body>
                    </Card>

                    {/* SECTION 3: Bottom Summary Block */}
                    {exchangeReturnProducts.length > 0 && (
                        <Card className="border-0 shadow-sm" style={{ borderRadius: "8px" }}>
                            <Card.Body className="p-3">
                                <Row className="align-items-center g-3">
                                    <Col md={4}>
                                        <div className="p-2 rounded border text-center" style={{ background: exchangeBalance > 0 ? "#fff5f5" : exchangeBalance < 0 ? "#f6fff6" : "#f8f9fa" }}>
                                            <span className="text-muted d-block small fw-bold">{t("Ledger Calculation")}</span>
                                            <span className={`fs-5 fw-bold ${exchangeBalance > 0 ? "text-danger" : exchangeBalance < 0 ? "text-success" : "text-dark"}`}>
                                                {exchangeBalance > 0 ? `+ ${formatAmount(exchangeBalance)} due` : exchangeBalance < 0 ? `- ${formatAmount(Math.abs(exchangeBalance))} refund` : "0.00 balanced"}
                                            </span>
                                        </div>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="small fw-bold text-secondary mb-1">{t("Exchange / Payment Date")}</Form.Label>
                                            <Form.Control type="date" className="form-control-sm" value={exchangeReturnDate} onChange={(e) => setExchangeReturnDate(e.target.value)} />
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        {/* Modified line: If there is an outstanding payment OR a refund balance, show Payment Method selection */}
                                        {exchangeBalance !== 0 ? (
                                            <Form.Group>
                                                <Form.Label className="small fw-bold text-secondary mb-1">
                                                    {exchangeBalance > 0 ? t("Payment Method") : t("Refund Method")} <span className="text-danger">*</span>
                                                </Form.Label>
                                                <select className="form-select form-select-sm" value={exchangePaymentMethodId} onChange={(e) => setExchangePaymentMethodId(e.target.value)}>
                                                    <option value="">{t("Select Method")}</option>
                                                    {paymentMethods.map(pm => <option key={pm.id} value={pm.id}>{pm.name}</option>)}
                                                </select>
                                            </Form.Group>
                                        ) : (
                                            <Form.Group>
                                                <Form.Label className="small fw-bold text-secondary mb-1">{t("Optional Note")}</Form.Label>
                                                <Form.Control type="text" className="form-control-sm" value={exchangeReturnReason} onChange={(e) => setExchangeReturnReason(e.target.value)} placeholder={t("Reason description...")} />
                                            </Form.Group>
                                        )}
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    )}
                </Form>
            </Modal.Body>

            <Modal.Footer className="bg-white border-top p-3 d-flex justify-content-between">
                <button type="button" className="btn btn-link link-secondary text-decoration-none p-0" onClick={onHide}>{t("Cancel")}</button>
                <ButtonComponent
                    className="btn btn-dark px-4 fw-bold"
                    onClick={handleExchangeSubmit}
                    disabled={exchangeSubmitting || exchangeReturnProducts.length === 0}
                    text={exchangeSubmitting ? t("Processing...") : t("Save Exchange Transaction")}
                />
            </Modal.Footer>
        </Modal>
    );
};

export default ExchangeModal;